import React, { useState } from 'react';
import {
	Card,
	Col,
	Form,
	Row,
	Skeleton,
} from 'antd';
import { useMutation } from 'react-query';
import { CSVLink } from 'react-csv';
import { ExportOutlined } from '@ant-design/icons';
import { ExportTypes } from '@shared/utils/constants';
import type { GetExportResponse } from '@api/features/misc/getExport';
import SimpleScreen from '@client/components/screens/SimpleScreen';
import Select from '@client/components/Select';
import { getExport } from '@client/lib/api';
import { formatDate } from '@client/utils/formatDate';
import Button from '@client/components/Button';
import RangePicker from '@client/components/RangePicker';
import ExportForm from './forms/ExportForm';

const ExportScreen = () => {
	const [exportType, setExportType] = useState<ExportTypes | null>(null);
	const [loading, setLoading] = useState(false);
	const [exportData, setExportData] = useState<GetExportResponse | undefined>();

	const [form] = Form.useForm();

	const previewMutator = useMutation(
		{
			mutationFn: () => getExport(exportType, form.getFieldsValue(true)),
			onMutate: () => setLoading(true),
			onSettled: async (result) => {
				setExportData(result);
				setLoading(false);
			},
		},
	);

	return (
		<SimpleScreen
			rootPageTitle="Home"
			title="Export"
		>
			<Row gutter={[16, 16]}>
				<Col span={12}>
					<Card title="Export Configurator">
						<Form
							form={form}
							onValuesChange={(v) => {
								if (v.exportType != null) {
									setExportType(v.exportType);
								}

								previewMutator.mutate();
							}}
							layout="vertical"
						>
							<Form.Item label="What would you like to export?" name="exportType">
								<Select
									placeholder="Select export type"
									options={Object.keys(ExportTypes).map((key) => ({
										label: ExportTypes[key as keyof typeof ExportTypes],
										value: ExportTypes[key as keyof typeof ExportTypes],
									}))}
								/>
							</Form.Item>
							{exportType == null ? (
								<Skeleton />
							) : (
								<>
									<ExportForm type={exportType} />
									<Form.Item label="Select timeframe (optional)" name="timeRange">
										<RangePicker placeholder={['Start date', 'End date']} />
									</Form.Item>
									<Form.Item label="Include previously exported items" name="includePreviouslyExported">
										<Select
											defaultValue={false}
											placeholder="Select option"
											options={[
												{ label: 'Yes', value: true },
												{ label: 'No', value: false },
											]}
										/>
									</Form.Item>
								</>
							)}
						</Form>
						{exportData != null && (
							<p>{`${exportData.count} entries found`}</p>
						)}
						{exportData != null && (
							<CSVLink
								key="export-csv"
								data={exportData?.exportData ?? []}
								filename={`ClearVoyage Export ${formatDate(new Date())}.csv`}
								separator=","
								uFEFF={false}
							>
								<Button
									onClick={() => {
										form.setFieldValue('final', true);
										previewMutator.mutate();
									}}
									loading={loading}
									disabled={exportData.count === 0}
									icon={(<ExportOutlined />)}
								>
									Export CSV
								</Button>
							</CSVLink>
						)}
					</Card>
				</Col>
			</Row>
		</SimpleScreen>
	);
};

export default ExportScreen;
